import { cn } from "@/lib/utils";
import type { ConversationContextSize } from "@cs_teamwaydev/mailvista-sdk_react";
import { useTranslation } from "react-i18next";

const CONTEXT_ORANGE_RATIO = 0.5;
const CONTEXT_RED_RATIO = 0.8;

type ContextLevel = "green" | "orange" | "red";

const contextLevel = (size: ConversationContextSize): ContextLevel => {
    const ratio = size.used / size.max;
    if (ratio >= CONTEXT_RED_RATIO) return "red";
    if (ratio >= CONTEXT_ORANGE_RATIO) return "orange";

    return "green";
};

const percentOf = (tokens: number, max: number) =>
    Math.min(100, Math.round((tokens / max) * 100));

const SLICE_COLOR: Record<ContextLevel, string> = {
    green: "fill-primary-dark",
    orange: "fill-warning-dark",
    red: "fill-error-dark",
};

const PIE_RADIUS = 7;

const slicePath = (percent: number) => {
    const angle = (percent / 100) * 2 * Math.PI;
    const x = 8 + PIE_RADIUS * Math.sin(angle);
    const y = 8 - PIE_RADIUS * Math.cos(angle);

    return `M8 8V1A${PIE_RADIUS} ${PIE_RADIUS} 0 ${percent > 50 ? 1 : 0} 1 ${x} ${y}Z`;
};

const ContextGauge = ({ size }: { size?: ConversationContextSize }) => {
    const { t } = useTranslation();
    if (!size) return null;

    const percent = percentOf(size.used, size.max);
    const level = contextLevel(size);

    return (
        <p
            className="flex flex-none items-center gap-2 text-sm text-assistant-muted"
            data-level={level}
        >
            <svg aria-hidden height={16} viewBox="0 0 16 16" width={16}>
                <circle
                    className="fill-assistant-lavender stroke-assistant-outline"
                    cx={8}
                    cy={8}
                    r={PIE_RADIUS}
                />
                {percent >= 100 ? (
                    <circle
                        className={SLICE_COLOR[level]}
                        cx={8}
                        cy={8}
                        r={PIE_RADIUS}
                    />
                ) : (
                    percent > 0 && (
                        <path
                            className={cn(SLICE_COLOR[level])}
                            d={slicePath(percent)}
                        />
                    )
                )}
            </svg>
            {t("vistassistant.contextGauge.session", { percent })}
        </p>
    );
};

export default ContextGauge;
